import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '@chakra-ui/styled-system'
import { keyframes } from '@emotion/react'
import { sharedComponentColorKeys } from '~/components/shared/componentStyles/_colors'
import { asChakraVariable, themeColorSectionKeys } from '~/themes/colors/colors'
import { Size } from '~/types/ThemeTypes'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

export enum QuickActionButtonVariants {
  Default_400 = 'Default_400',
  Default_300 = 'Default_300',
  Default_200 = 'Default_200',
  Default_150 = 'Default_150',
  Default_100 = 'Default_100',
  Default_000 = 'Default_000',
  Destructive_300 = 'Destructive_300',
  Destructive_200 = 'Destructive_200',
  Destructive_100 = 'Destructive_100',
  Active_200 = 'Active_200',
  Greyed_Out = 'Greyed_Out',
  Pulsate_Blue = 'Pulsate_Blue',
}

const Colors = sharedComponentColorKeys.QuickActionButton

const {
  Button: {
    FocusVisible: { BoxShadow: BoxShadowColor },
  },
} = themeColorSectionKeys.defaults

const Default_400_Gradients = {
  Base: `linear-gradient(135deg, ${asChakraVariable(Colors.Default_400.Background.GradientColor1)} 19.69%, ${asChakraVariable(Colors.Default_400.Background.GradientColor2)} 57.36%, ${asChakraVariable(Colors.Default_400.Background.GradientColor3)} 87.57%)`,
  Hover: `linear-gradient(135deg, ${asChakraVariable(Colors.Default_400.Hover.Background.GradientColor1)} 18.5%, ${asChakraVariable(Colors.Default_400.Hover.Background.GradientColor2)} 55.5%, ${asChakraVariable(Colors.Default_400.Hover.Background.GradientColor3)} 89.5%)`,
  Active: `linear-gradient(135deg, ${asChakraVariable(Colors.Default_400.Active.Background.GradientColor1)} 23.8%, ${asChakraVariable(Colors.Default_400.Active.Background.GradientColor2)} 80.31%)`,
}

const quickActionButtonStyleParts = ['tooltip', 'iconButton', 'icon'] as const

const helpers = createMultiStyleConfigHelpers(quickActionButtonStyleParts)

const pulsateAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`

export const QuickActionButtonStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    tooltip: {
      border: '1px solid transparent',
      borderRadius: '.25rem',
      paddingY: '.25rem',
      paddingX: '.75rem',
    },
    iconButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 'full',
      transition: 'all 0.15s ease-in-out',
      _focusVisible: {
        boxShadow: `${asChakraVariable(BoxShadowColor)} 0px 0px 0px 2px`,
      },
    },
    icon: {},
  },
  variants: {
    [QuickActionButtonVariants.Default_400]: {
      iconButton: {
        '@keyframes hover': {
          from: {
            background: Default_400_Gradients.Base,
          },
          to: {
            background: Default_400_Gradients.Hover,
          },
        },
        '@keyframes active': {
          from: {
            background: Default_400_Gradients.Hover,
          },
          to: {
            background: Default_400_Gradients.Active,
          },
        },
        position: 'relative',
        transition: 'all 0.15s ease-in-out',
        background: Default_400_Gradients.Base,
        _before: {
          position: 'absolute',
          zIndex: 1,
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          opacity: 0,
          content: '""',
          display: 'block',
          borderRadius: 'full',
          transition: 'opacity 0.25s ease-in-out',
          background: Default_400_Gradients.Hover,
        },
        _hover: {
          animation: 'hover 0.3s both ease-in-out',
          _before: {
            opacity: 1,
          },
        },
        _after: {
          position: 'absolute',
          zIndex: 1,
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          opacity: 0,
          content: '""',
          display: 'block',
          borderRadius: 'full',
          transition: 'opacity 0.15s ease-in-out',
          background: Default_400_Gradients.Active,
        },
        _active: {
          animation: 'active 0.3s both ease-in-out',
          _after: {
            opacity: 1,
          },
        },
        _disabled: {
          background: Colors.Default_400.Disabled.Background,
          cursor: 'default',
        },
        _focusVisible: {
          animation: 'hover 0.3s both ease-in-out',
          _before: {
            opacity: 1,
          },
          boxShadow: 'none',
        },
      },
      icon: {
        position: 'relative',
        zIndex: 2,
        color: Colors.Default_400.Color,
        _groupHover: {
          color: Colors.Default_400.Hover.Color,
        },
        _groupActive: {
          color: Colors.Default_400.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Default_400.Disabled.Color,
        },
      },
    },
    [QuickActionButtonVariants.Default_300]: {
      iconButton: {
        backgroundColor: Colors.Default_300.Background,
        _hover: {
          backgroundColor: Colors.Default_300.Hover.Background,
          boxShadow: `${asChakraVariable(Colors.Default_300.Hover.BoxShadow)} 0px 4px 8px 0px`,
        },
        _active: {
          backgroundColor: Colors.Default_300.Active.Background,
          boxShadow: `${asChakraVariable(Colors.Default_300.Active.BoxShadow)} 0px 4px 8px 0px`,
        },
        _disabled: {
          backgroundColor: Colors.Default_300.Disabled.Background,
          boxShadow: 'none',
          cursor: 'default',
        },
      },
      icon: {
        color: Colors.Default_300.Color,
        _groupHover: {
          color: Colors.Default_300.Hover.Color,
        },
        _groupActive: {
          color: Colors.Default_300.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Default_300.Disabled.Color,
        },
      },
    },
    [QuickActionButtonVariants.Default_200]: {
      iconButton: {
        backgroundColor: Colors.Default_200.Background,
        _hover: {
          backgroundColor: Colors.Default_200.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Default_200.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Default_200.Disabled.Background,
          cursor: 'default',
        },
      },
      icon: {
        color: Colors.Default_200.Color,
        _groupHover: {
          color: Colors.Default_200.Hover.Color,
        },
        _groupActive: {
          color: Colors.Default_200.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Default_200.Disabled.Color,
        },
      },
    },
    [QuickActionButtonVariants.Default_150]: {
      iconButton: {
        backgroundColor: Colors.Default_150.Background,
        _hover: {
          backgroundColor: Colors.Default_150.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Default_150.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Default_150.Disabled.Background,
          cursor: 'default',
        },
      },
      icon: {
        color: Colors.Default_150.Color,
        _groupHover: {
          color: Colors.Default_150.Hover.Color,
        },
        _groupActive: {
          color: Colors.Default_150.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Default_150.Disabled.Color,
        },
      },
    },
    [QuickActionButtonVariants.Default_100]: {
      iconButton: {
        backgroundColor: Colors.Default_100.Background,
        _hover: {
          backgroundColor: Colors.Default_100.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Default_100.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Default_100.Disabled.Background,
          cursor: 'default',
        },
      },
      icon: {
        color: Colors.Default_100.Color,
        _groupHover: {
          color: Colors.Default_100.Hover.Color,
        },
        _groupActive: {
          color: Colors.Default_100.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Default_100.Disabled.Color,
        },
      },
    },
    [QuickActionButtonVariants.Default_000]: {
      iconButton: {
        backgroundColor: Colors.Default_100.Background,
        _hover: {
          backgroundColor: Colors.Default_100.Background,
        },
        _active: {
          backgroundColor: Colors.Default_100.Background,
        },
        _disabled: {
          backgroundColor: Colors.Default_100.Background,
          cursor: 'default',
        },
      },
      icon: {
        color: Colors.Default_100.Color,
        _groupHover: {
          color: Colors.Default_100.Hover.Color,
        },
        _groupActive: {
          color: Colors.Default_100.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Default_100.Disabled.Color,
        },
      },
    },
    [QuickActionButtonVariants.Destructive_300]: {
      iconButton: {
        backgroundColor: Colors.Destructive_300.Background,
        _hover: {
          backgroundColor: Colors.Destructive_300.Hover.Background,
          boxShadow: `${asChakraVariable(Colors.Destructive_300.Hover.BoxShadow)} 0px 4px 8px 0px`,
        },
        _active: {
          backgroundColor: Colors.Destructive_300.Active.Background,
          boxShadow: `${asChakraVariable(Colors.Destructive_300.Active.BoxShadow)} 0px 4px 8px 0px`,
        },
        _disabled: {
          backgroundColor: Colors.Destructive_300.Disabled.Background,
          cursor: 'default',
        },
      },
      icon: {
        color: Colors.Destructive_300.Color,
        _groupHover: {
          color: Colors.Destructive_300.Hover.Color,
        },
        _groupActive: {
          color: Colors.Destructive_300.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Destructive_300.Disabled.Color,
        },
      },
    },
    [QuickActionButtonVariants.Destructive_200]: {
      iconButton: {
        backgroundColor: Colors.Destructive_200.Background,
        _hover: {
          backgroundColor: Colors.Destructive_200.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Destructive_200.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Destructive_200.Disabled.Background,
          cursor: 'default',
        },
      },
      icon: {
        color: Colors.Destructive_200.Color,
        _groupHover: {
          color: Colors.Destructive_200.Hover.Color,
        },
        _groupActive: {
          color: Colors.Destructive_200.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Destructive_200.Disabled.Color,
        },
      },
    },
    [QuickActionButtonVariants.Destructive_100]: {
      iconButton: {
        backgroundColor: Colors.Destructive_100.Background,
        _hover: {
          backgroundColor: Colors.Destructive_100.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Destructive_100.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Destructive_100.Disabled.Background,
          cursor: 'default',
        },
      },
      icon: {
        color: Colors.Destructive_100.Color,
        _groupHover: {
          color: Colors.Destructive_100.Hover.Color,
        },
        _groupActive: {
          color: Colors.Destructive_100.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Destructive_100.Disabled.Color,
        },
      },
    },
    [QuickActionButtonVariants.Greyed_Out]: {
      iconButton: {
        backgroundColor: 'transparent',
        _hover: {
          backgroundColor: 'transparent',
        },
        _active: {
          backgroundColor: 'transparent',
        },
        _disabled: {
          backgroundColor: 'transparent',
          cursor: 'default',
        },
      },
      icon: {
        color: 'gray.600',
        _disabled: {
          color: 'gray.600',
        },
      },
    },
    [QuickActionButtonVariants.Pulsate_Blue]: {
      iconButton: {
        backgroundColor: 'transparent',
        _hover: {
          backgroundColor: 'transparent',
        },
        _active: {
          backgroundColor: 'transparent',
        },
        _disabled: {
          backgroundColor: 'transparent',
          cursor: 'default',
        },
      },
      icon: {
        color: 'blue.800',
        animation: `${pulsateAnimation} 1.5s infinite`,
        _disabled: {
          color: 'blue.800',
        },
      },
    },

    [QuickActionButtonVariants.Active_200]: {
      iconButton: {
        backgroundColor: Colors.Active_200.Background,
        _hover: {
          backgroundColor: Colors.Active_200.Hover.Background,
        },
        _active: {
          backgroundColor: Colors.Active_200.Active.Background,
        },
        _disabled: {
          backgroundColor: Colors.Active_200.Disabled.Background,
          cursor: 'default',
        },
      },
      icon: {
        color: Colors.Active_200.Color,
        _groupHover: {
          color: Colors.Active_200.Hover.Color,
        },
        _groupActive: {
          color: Colors.Active_200.Active.Color,
        },
        _groupDisabled: {
          color: Colors.Active_200.Disabled.Color,
        },
      },
    },
  },
  sizes: {
    [Size.xs]: {
      iconButton: {
        boxSize: '1.5rem',
      },
      icon: {
        boxSize: '1rem',
      },
    },
    [Size.sm]: {
      iconButton: {
        boxSize: '1.75rem',
      },
      icon: {
        boxSize: '1rem',
      },
    },
    [Size.md]: {
      iconButton: {
        boxSize: '2.5rem',
      },
      icon: {
        boxSize: '1.25rem',
      },
    },
    [Size.lg]: {
      iconButton: {
        boxSize: '3rem',
      },
      icon: {
        boxSize: '1.5rem',
      },
    },
    [Size.xl]: {
      iconButton: {
        boxSize: '5rem',
      },
      icon: {
        boxSize: '2.25rem',
      },
    },
  },
  defaultProps: {
    variant: QuickActionButtonVariants.Default_300,
    size: Size.md,
  },
})

export const useQuickActionButtonStyles = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig<typeof quickActionButtonStyleParts>(
    getName({ QuickActionButtonStyle }),
    props,
  )
}
